import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Loader2, Home, Plus, Search
} from "lucide-react";
import { API } from "@/App";

const PROPERTY_STATUS_OPTIONS = [
  { value: "on_market", label: "On Market", color: "bg-blue-500 text-white" },
  { value: "sold_stc", label: "Sold STC", color: "bg-amber-500 text-white" },
  { value: "sold", label: "Sold", color: "bg-green-500 text-white" },
  { value: "exchanged", label: "Exchanged", color: "bg-emerald-600 text-white" },
  { value: "completed", label: "Completed", color: "bg-green-700 text-white" },
  { value: "aip", label: "Agreement in Principle", color: "bg-orange-500 text-white" },
  { value: "mortgage_offered", label: "Mortgage Offered", color: "bg-teal-500 text-white" },
  { value: "awaiting_exchange", label: "Awaiting Exchange", color: "bg-purple-500 text-white" },
];

const BUYER_CHAIN_STATUS_OPTIONS = [
  { value: "no_chain", label: "No Chain", color: "bg-green-100 text-green-800" },
  { value: "chain_free", label: "Chain Free", color: "bg-green-100 text-green-800" },
  { value: "chain_complete", label: "Chain Complete", color: "bg-blue-100 text-blue-800" },
  { value: "chain_incomplete", label: "Chain Incomplete", color: "bg-amber-100 text-amber-800" },
  { value: "awaiting_info", label: "Awaiting Info", color: "bg-slate-100 text-slate-800" },
  { value: "ftb", label: "First Time Buyer (FTB)", color: "bg-purple-100 text-purple-800" },
  { value: "investor", label: "Investor/Cash", color: "bg-emerald-100 text-emerald-800" }
];

const ChainBuilderPage = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [chainProperties, setChainProperties] = useState([]);
  
  // Modal state
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [saving, setSaving] = useState(false);
  const [insertPosition, setInsertPosition] = useState(null); // Position to insert new property
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingChainId, setDeletingChainId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    property_address: "",
    postcode: "",
    vendor_name: "",
    buyer_name: "",
    agent: "Goodacres",
    price_agreed: "",
    status: "on_market",
    buyer_chain_status: "awaiting_info",
    has_problems: false,
    notes: ""
  });

  const fetchCaseData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sales/cases/${caseId}`, { headers });
      setCaseData(response.data);
    } catch (error) {
      console.error("Error fetching case:", error);
      toast.error("Failed to load case data");
    }
  }, [caseId]);

  const fetchChainProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sales/chain/${caseId}`, { headers });
      setChainProperties(response.data.chain || []);
    } catch (error) {
      console.log("No chain data found");
      setChainProperties([]);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (caseId) {
      fetchCaseData();
      fetchChainProperties();
    }
  }, [caseId, fetchCaseData, fetchChainProperties]);

  const resetForm = () => {
    setFormData({
      property_address: "",
      postcode: "",
      vendor_name: "",
      buyer_name: "",
      agent: "Goodacres",
      price_agreed: "",
      status: "on_market",
      buyer_chain_status: "awaiting_info",
      has_problems: false,
      notes: ""
    });
    setEditingProperty(null);
    setInsertPosition(null);
  };

  const openAddModal = (position = null) => {
    resetForm();
    setInsertPosition(position);
    setShowPropertyModal(true);
  };

  const openEditModal = (property) => {
    setEditingProperty(property);
    setInsertPosition(null);
    setFormData({
      property_address: property.property_address || "",
      postcode: property.postcode || "",
      vendor_name: property.vendor_name || "",
      buyer_name: property.buyer_name || "",
      agent: property.agent || "Goodacres",
      price_agreed: property.price_agreed || "",
      status: property.status || "on_market",
      buyer_chain_status: property.buyer_chain_status || "awaiting_info",
      has_problems: property.has_problems || false,
      notes: property.notes || ""
    });
    setShowPropertyModal(true);
  };

  const handleSaveProperty = async () => {
    if (!formData.property_address.trim()) {
      toast.error("Property address is required");
      return;
    }
    
    setSaving(true);
    try {
      // Extract clean primitive data only
      const cleanData = {
        property_address: String(formData.property_address || ""),
        postcode: String(formData.postcode || ""),
        vendor_name: String(formData.vendor_name || ""),
        buyer_name: String(formData.buyer_name || ""),
        agent: String(formData.agent || "Goodacres"),
        price_agreed: String(formData.price_agreed || ""),
        status: String(formData.status || "on_market"),
        buyer_chain_status: String(formData.buyer_chain_status || "awaiting_info"),
        has_problems: formData.has_problems === true,
        notes: String(formData.notes || "")
      };
      
      const authToken = localStorage.getItem("token");
      
      if (editingProperty) {
        const response = await fetch(`${API}/sales/chain/${editingProperty.chain_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(cleanData)
        });
        if (!response.ok) throw new Error('Failed to update');
        toast.success("Chain property updated");
      } else {
        const newPosition = insertPosition !== null ? insertPosition : chainProperties.length;
        const postData = {
          property_address: cleanData.property_address,
          postcode: cleanData.postcode,
          vendor_name: cleanData.vendor_name,
          buyer_name: cleanData.buyer_name,
          agent: cleanData.agent,
          price_agreed: cleanData.price_agreed,
          status: cleanData.status,
          buyer_chain_status: cleanData.buyer_chain_status,
          has_problems: cleanData.has_problems,
          notes: cleanData.notes,
          case_id: String(caseId),
          position: Number(newPosition)
        };
        
        const response = await fetch(`${API}/sales/chain`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(postData)
        });
        if (!response.ok) throw new Error('Failed to save');
        toast.success("Property added to chain");
      }
      setShowPropertyModal(false);
      resetForm();
      fetchChainProperties();
    } catch (error) {
      console.error("Error saving chain property:", error);
      toast.error(error.message || "Failed to save chain property");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProperty = async (chainId) => {
    setDeletingChainId(chainId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deletingChainId) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/sales/chain/${deletingChainId}`, { headers });
      toast.success("Property removed from chain");
      fetchChainProperties();
    } catch (error) {
      console.error("Error deleting chain property:", error);
      toast.error("Failed to remove property");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setDeletingChainId(null);
    }
  };

  const getStatusBadge = (status) => {
    // Default to sold_stc if no status set
    const statusValue = status || "sold_stc";
    const option = PROPERTY_STATUS_OPTIONS.find(o => o.value === statusValue);
    return option ? (
      <Badge className={option.color}>{option.label}</Badge>
    ) : (
      <Badge className="bg-amber-500 text-white">Sold STC</Badge>
    );
  };

  const getProblemBadge = (hasProblems) => {
    if (hasProblems) {
      return (
        <div className="absolute -top-2 -left-2 z-10">
          <Badge className="bg-red-500 text-white transform -rotate-45 text-xs px-2 py-1 shadow-sm">
            ISSUES
          </Badge>
        </div>
      );
    }
    return (
      <div className="absolute -top-2 -left-2 z-10">
        <Badge className="bg-green-500 text-white transform -rotate-45 text-xs px-2 py-1 shadow-sm">
          NO PROBLEMS
        </Badge>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/sales/case/${caseId}/hub`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Case
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <div>
              <h1 className="text-lg font-bold text-slate-800">Chain Builder</h1>
              {caseData && (
                <p className="text-sm text-slate-500">{caseData.address_line1}, {caseData.postcode}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Add Property Button at Top */}
        <div className="flex justify-center mb-8">
          <Button 
            onClick={openAddModal}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full shadow-lg"
            data-testid="add-chain-property-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Property
          </Button>
        </div>

        {/* Down Arrow from button */}
        {chainProperties.length > 0 && (
          <div className="flex justify-center mb-4">
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-8 bg-slate-300"></div>
              <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[12px] border-t-slate-300"></div>
            </div>
          </div>
        )}

        {/* Chain Properties */}
        {chainProperties.length === 0 ? (
          <Card className="max-w-xl mx-auto">
            <CardContent className="py-12 text-center">
              <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No chain properties added yet</p>
              <p className="text-sm text-slate-400 mt-2">Click "Add Property" above to start building the chain</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-0">
            {chainProperties.sort((a, b) => a.position - b.position).map((property, idx) => (
              <div key={property.chain_id || idx}>
                {/* Property Card */}
                <Card className="relative max-w-2xl mx-auto border border-slate-200 shadow-sm overflow-visible">
                  {/* Problem Badge (rotated diagonal) */}
                  {getProblemBadge(property.has_problems)}
                  
                  <CardContent className="p-0">
                    {/* Header with Address and Status */}
                    <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-slate-900 text-lg">
                          {property.property_address || "Address TBC"}
                        </h3>
                        {property.postcode && (
                          <p className="text-sm text-slate-500">{property.postcode}</p>
                        )}
                      </div>
                      {getStatusBadge(property.status)}
                    </div>

                    {/* Property Details with Image Placeholder */}
                    <div className="flex">
                      {/* Image Placeholder */}
                      <div className="w-36 h-32 bg-slate-100 flex-shrink-0 flex items-center justify-center m-4 rounded-lg overflow-hidden">
                        {property.main_photo ? (
                          <img 
                            src={property.main_photo} 
                            alt={property.property_address}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Home className="w-10 h-10 text-slate-300" />
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1 py-4 pr-6 space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-slate-500">Vendor</span>
                            <p className="font-medium text-slate-900">{property.vendor_name || "-"}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Agent</span>
                            <p className="font-medium text-slate-900">{property.agent || "Goodacres"}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Buyer</span>
                            <p className="font-medium text-slate-900">{property.buyer_name || "-"}</p>
                          </div>
                          {property.price_agreed && (
                            <div>
                              <span className="text-slate-500">Price</span>
                              <p className="font-medium text-green-600">£{Number(property.price_agreed).toLocaleString()}</p>
                            </div>
                          )}
                        </div>

                        {/* Latest Note */}
                        {property.notes && (
                          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-xs font-semibold text-amber-700 mb-1">Latest Note:</p>
                                <p className="text-sm text-slate-700 line-clamp-2">{property.notes}</p>
                              </div>
                              <button className="text-blue-600 text-xs font-medium hover:underline whitespace-nowrap ml-3">
                                More Details &gt;
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Down Arrow Connector */}
                    <div className="flex justify-center py-2">
                      <div className="flex flex-col items-center">
                        <div className="w-0.5 h-4 bg-slate-300"></div>
                        <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-slate-300"></div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openEditModal(property)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        data-testid={`view-details-${idx}`}
                      >
                        View Details
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openEditModal(property)}
                        data-testid={`edit-chain-${idx}`}
                      >
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleDeleteProperty(property.chain_id)}
                        data-testid={`delete-chain-${idx}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Add Onward Property Button - between cards or at end */}
                <div className="flex justify-center py-4">
                  <div className="flex flex-col items-center">
                    <div className="w-0.5 h-4 bg-slate-300"></div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openAddModal(property.position + 1)}
                      className="rounded-full border-dashed border-2 border-emerald-400 text-emerald-600 hover:bg-emerald-50 px-4"
                      data-testid={`add-onward-${idx}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Onward Property
                    </Button>
                    <div className="w-0.5 h-4 bg-slate-300"></div>
                    <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[12px] border-t-slate-300"></div>
                  </div>
                </div>
              </div>
            ))}

            {/* FTB indicator at bottom if applicable */}
            {chainProperties.some(p => p.buyer_chain_status === "ftb") && (
              <div className="flex justify-center mt-6">
                <div className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 rounded-lg shadow-sm">
                  <Search className="w-5 h-5 text-slate-500" />
                  <span className="font-bold text-slate-700">FTB</span>
                  <span className="text-sm text-slate-500">First Time Buyer</span>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add/Edit Property Modal */}
      <Dialog open={showPropertyModal} onOpenChange={setShowPropertyModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingProperty ? "Edit Chain Property" : "Add Property to Chain"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Property Address *</Label>
                <Input
                  value={formData.property_address}
                  onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                  placeholder="e.g. 9 Pine Close, Epsom"
                  data-testid="chain-property-address"
                />
              </div>
              
              <div>
                <Label>Postcode</Label>
                <Input
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  placeholder="e.g. KT19 8FN"
                />
              </div>
              
              <div>
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Vendor Name</Label>
                <Input
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                  placeholder="e.g. Mr. Smith"
                />
              </div>
              
              <div>
                <Label>Agent</Label>
                <Input
                  value={formData.agent}
                  onChange={(e) => setFormData({ ...formData, agent: e.target.value })}
                  placeholder="e.g. Goodacres"
                />
              </div>
              
              <div>
                <Label>Buyer Name</Label>
                <Input
                  value={formData.buyer_name}
                  onChange={(e) => setFormData({ ...formData, buyer_name: e.target.value })}
                  placeholder="e.g. Mr. Jones"
                />
              </div>
              
              <div>
                <Label>Price Agreed</Label>
                <Input
                  type="number"
                  value={formData.price_agreed}
                  onChange={(e) => setFormData({ ...formData, price_agreed: e.target.value })}
                  placeholder="e.g. 450000"
                />
              </div>
              
              <div>
                <Label>Buyer Chain Status</Label>
                <Select 
                  value={formData.buyer_chain_status} 
                  onValueChange={(v) => setFormData({ ...formData, buyer_chain_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUYER_CHAIN_STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="has_problems"
                  checked={formData.has_problems}
                  onChange={(e) => setFormData({ ...formData, has_problems: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <Label htmlFor="has_problems" className="cursor-pointer">Has Issues/Problems</Label>
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Latest update or notes about this property..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPropertyModal(false)}>
              Cancel
            </Button>
            <Button onClick={(e) => { e.preventDefault(); handleSaveProperty(); }} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingProperty ? "Update Property" : "Add to Chain"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Remove Property from Chain?</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 py-4">
            Are you sure you want to remove this property from the chain? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete} 
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Remove Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChainBuilderPage;
/* Build timestamp: 1771014706 */
